import { type ReactNode } from 'react'
import { createBrowserRouter, RouterProvider, Navigate, Outlet } from 'react-router-dom'
import { useAuth } from './context/AuthContext'
import Header from './components/Header'
import Footer from './components/Footer'
import { ToastProvider } from './components/ToastContainer'

function Layout({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-[var(--color-surface-subtle)] flex flex-col">
      {children}
      <Footer />
    </div>
  )
}

function AuthLayout({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--color-surface-subtle)] p-4">
      <div className="w-full max-w-md">{children}</div>
    </div>
  )
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth()

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--color-surface-subtle)]">
        <div className="animate-spin rounded-full h-10 w-10 border-4 border-[var(--color-brand-200)] border-t-[var(--color-brand-500)]" />
      </div>
    )
  }

  if (!user) {
    return <Navigate to="/login" replace />
  }

  return <>{children}</>
}

// Admin-only guard: requires is_admin=true
function AdminRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth()

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--color-surface-subtle)]">
        <div className="animate-spin rounded-full h-10 w-10 border-4 border-[var(--color-brand-200)] border-t-[var(--color-brand-500)]" />
      </div>
    )
  }

  if (!user || !user.is_admin) {
    return <Navigate to="/dashboard" replace />
  }

  return <>{children}</>
}

// Pages (imported here to keep router config together)
import LoginPage from './pages/LoginPage'
import RootRedirect from './pages/RootRedirect'
import RegisterPage from './pages/RegisterPage'
import UnderReviewPage from './pages/UnderReviewPage'
import DashboardPage from './pages/DashboardPage'
import CardsPage from './pages/CardsPage'
import TransactionsPage from './pages/TransactionsPage'
import CategoriesPage from './pages/CategoriesPage'

// Admin pages
import AdminLayout from './layouts/AdminLayout'
import AdminDashboard from './pages/AdminDashboard'
import AdminPending from './pages/AdminPending'
import AdminUsers from './pages/AdminUsers'
import AdminProfile from './pages/AdminProfile'
import AdminSettings from './pages/AdminSettings'
import AdminChangePassword from './pages/AdminChangePassword'

const router = createBrowserRouter([
  {
    path: '/',
    element: (
      <ProtectedRoute>
        <Layout>
          <Outlet />
        </Layout>
      </ProtectedRoute>
    ),
    children: [
      {
        index: true,
        element: <RootRedirect />,
      },
      {
        path: 'dashboard',
        element: (
          <ProtectedRoute>
            <Header />
            <DashboardPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'cards',
        element: (
          <ProtectedRoute>
            <Header />
            <CardsPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'transactions',
        element: (
          <ProtectedRoute>
            <Header />
            <TransactionsPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'categories',
        element: (
          <ProtectedRoute>
            <Header />
            <CategoriesPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'admin',
        element: (
          <AdminRoute>
            <AdminLayout />
          </AdminRoute>
        ),
        children: [
          {
            index: true,
            element: <AdminDashboard />,
          },
          {
            path: 'pending',
            element: <AdminPending />,
          },
          {
            path: 'users',
            element: <AdminUsers />,
          },
          {
            path: 'profile',
            element: <AdminProfile />,
          },
          {
            path: 'settings',
            element: <AdminSettings />,
          },
          {
            path: 'change-password',
            element: <AdminChangePassword />,
          },
        ],
      },
    ],
  },
  {
    path: '/login',
    element: (
      <AuthLayout>
        <LoginPage />
      </AuthLayout>
    ),
  },
  {
    path: '/register',
    element: (
      <AuthLayout>
        <RegisterPage />
      </AuthLayout>
    ),
  },
  {
    path: '/under-review',
    element: (
      <AuthLayout>
        <UnderReviewPage />
      </AuthLayout>
    ),
  },
])

export default function App() {
  return (
    <ToastProvider>
      <RouterProvider router={router} />
    </ToastProvider>
  )
}
